import React, {
  ReactElement,
  useState,
  useRef,
  useEffect,
  useCallback,
  SyntheticEvent,
  useMemo,
} from 'react';
import { Box, Text } from 'rebass/styled-components';

import {
  SlidePanel,
  Loader,
  SearchableList,
  ListBody,
  ListSearch,
  List,
  FormInput,
  FormSelect,
} from 'ui/patterns';
import { Button, Cluster, FormLabel, Stack } from 'ui/components';

import { useGetAccountListForImportEmbedProcedures } from 'hooks';

import * as Icon from 'assets/icons';

import { showErrorToast } from 'utils/showToast';

import { HyperlinkProps } from './hyperlinkTypes';
import {
  linkImportAccountList,
  ProcedureDetailsInList,
} from 'types/procedureBuilder';

function Hyperlink({
  showPanel,
  togglePanel: toggle,
  accountId,
  selectedWords,
  tagsList,
}: HyperlinkProps): ReactElement {
  const [isVisible, setIsVisible] = useState(false);
  //Search
  const [keyword, setKeyword] = useState<string>('');
  const [procedureKeyword, setProcedureKeyword] = useState<string>('');
  //Handling Selection
  const [activeAccount, setActiveAccount] = useState<number>();
  const [activeProcedure, setActiveProcedure] = useState<number>();

  const [procedureList, setProcedureList] = useState<
    Array<ProcedureDetailsInList>
  >([]);
  const [internalExternalLink, setInternalExternalLink] = useState(0);
  //Setting urls based on selection
  const [externalUrl, setExternalUrl] = useState('');
  const [selectedExternalUrlType, setSelectedExternalUrlTypes] =
    useState<number>(1);
  const [linkText, setLinkText] = useState<string>('');
  const [tag, setTag] = useState<{ Id: string; html: string } | undefined>();
  const [showSelectedAccountAndProcedure, setShowSelectedAccountAndProcedure] =
    useState<{
      linkedAccount: string;
      procedureName: string;
      link: string;
    }>();

  const tbodyRef = useRef() as React.MutableRefObject<HTMLDivElement>;

  const {
    data: accountsData,
    fetchNextPage,
    hasNextPage,
    isLoading: isAccountListLoading,
    isFetching,
  } = useGetAccountListForImportEmbedProcedures(accountId, keyword);

  useEffect(() => {
    setTimeout(() => {
      setIsVisible(showPanel);
    }, 10);
  }, [showPanel]);

  useEffect(() => {
    setLinkText(selectedWords);
  }, [selectedWords, linkText]);

  const accountList = useMemo(() => {
    return accountsData?.pages?.map((dataItem: any) => dataItem.Data).flat();
  }, [accountsData]);

  const handleScroll = useCallback(
    (e: any) => {
      if (tbodyRef.current === e.target) {
        const bottom =
          e.target.clientHeight - 10 <
            e.target.scrollHeight - e.target.scrollTop &&
          e.target.scrollHeight - e.target.scrollTop <
            e.target.clientHeight + 10;
        if (bottom) {
          hasNextPage && fetchNextPage();
        }
      }
    },
    [fetchNextPage, hasNextPage],
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [handleScroll]);

  useEffect(() => {
    const selectedAccount = accountList?.filter(
      item => item.AccountId === activeAccount,
    );
    const accountProcedures =
      selectedAccount &&
      selectedAccount[0] &&
      selectedAccount[0].ProcedureDetails;
    if (accountProcedures) {
      const data = [...accountProcedures];
      const result = accountProcedures.filter(
        (
          item: ProcedureDetailsInList,
          index: any,
          data: Array<ProcedureDetailsInList>,
        ) => {
          if (
            index ===
            data.findIndex(
              (singleItem: ProcedureDetailsInList) =>
                singleItem.ProcedureId === item.ProcedureId,
            )
          )
            return item;
        },
      );
      setProcedureList(result);
    }
  }, [accountList, activeAccount]);

  useEffect(() => {
    if (!!showPanel) {
      const hrefTag = (
        window as any
      ).tinymce?.activeEditor?.selection?.getNode()?.href;
      if (hrefTag) {
        const procedurePath = hrefTag.split('/');
        if (procedurePath.indexOf('accountfolders') > -1) {
          if (procedurePath.indexOf('procedures') > -1) {
            setInternalExternalLink(3);
            const linkedAccount = procedurePath[procedurePath.length - 3];
            const procedureName = procedurePath[procedurePath.length - 1];
            setShowSelectedAccountAndProcedure({
              linkedAccount: linkedAccount,
              procedureName: procedureName.replace('%20', ''),
              link: hrefTag,
            });
          }
        } else if (hrefTag.indexOf('#hyper_link') > -1) {
          const tagId = hrefTag.split('#').pop(-1);
          const tagItem = tagsList.find(item => item.Id === tagId);
          setTag(tagItem);
        } else if (
          hrefTag.startsWith('http://') ||
          hrefTag.startsWith('https://')
        ) {
          setInternalExternalLink(2);
          if (hrefTag.startsWith('https://')) {
            setSelectedExternalUrlTypes(3);
            setExternalUrl(hrefTag.replace('https://', ''));
          } else {
            setSelectedExternalUrlTypes(2);
            setExternalUrl(hrefTag.replace('http://', ''));
          }
        }
      }
    }
  }, [showPanel, tagsList]);

  function togglePanel() {
    setIsVisible(false);
    setTimeout(() => {
      toggle();
    }, 500);
  }

  function reset() {
    setKeyword('');
    setProcedureKeyword('');
    setActiveAccount(0);
    setActiveProcedure(0);
    setInternalExternalLink(0);
    setExternalUrl('');
    setSelectedExternalUrlTypes(1);
    setLinkText('');
    setTag(undefined);
    setShowSelectedAccountAndProcedure(undefined);
  }

  function setAsHyperLink() {
    let url = '';
    if (internalExternalLink === internalExternalLinks[1].id) {
      if (
        selectedExternalUrlType === externalUrlTypes[1].id ||
        selectedExternalUrlType === externalUrlTypes[2].id
      ) {
        const prefix = externalUrlTypes.find(
          item => item.id === selectedExternalUrlType,
        )?.name;
        if (externalUrl) url = `${prefix}://${externalUrl}`;
        else showErrorToast({ message: 'Please enter the url' });
      } else {
        showErrorToast({ message: 'Please select a external url type' });
      }
    } else if (internalExternalLink === internalExternalLinks[2].id) {
      if (activeAccount) {
        if (activeProcedure) {
          const procedure = procedureList.find(
            item => item.ProcedureId === activeProcedure,
          );
          const account =
            accountList &&
            accountList.find(
              (i: linkImportAccountList) => i && i.AccountId === activeAccount,
            );
          url = `/api/procedures/V1/accountfolders/${account && account.CID}-${
            account && account.SID
          }-${account && account.PID}${'/Policies/'}${
            procedure?.ProcedureName
          }`;
        } else {
          showErrorToast({ message: 'Please select a procedure' });
        }
      } else {
        showErrorToast({ message: 'Please select a account' });
      }
    } else {
      showErrorToast({ message: 'Please select a link' });
    }
    if (url) {
      const anchor = '<a href="' + url + '">' + linkText + '</a>';
      (window as any).tinymce?.activeEditor?.insertContent(anchor);
      togglePanel();
      reset();
    }
  }

  const procedureActions = () => (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          togglePanel();
          reset();
        }}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={() => {
          setAsHyperLink();
        }}
      >
        Apply
      </Button>
    </>
  );

  const handleSearchKeyword = (e: SyntheticEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setKeyword(value);
  };

  const handleSelectedProcedure = (procedure: ProcedureDetailsInList) => {
    setActiveProcedure(procedure?.ProcedureId);
  };

  const handleProcedureSearch = (e: SyntheticEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setProcedureKeyword(value);
    let newProcedures: Array<ProcedureDetailsInList> | undefined = [];
    const selectedAccount = accountList?.filter(
      item => item.AccountId === activeAccount,
    );
    const accountProcedures =
      selectedAccount &&
      selectedAccount[0] &&
      selectedAccount[0].ProcedureDetails;
    newProcedures =
      accountProcedures &&
      accountProcedures.filter((item: any) => {
        const searchTerm = `${item.ProcedureName}`;
        if (searchTerm.toLowerCase().includes(value.toLowerCase())) return item;
      });
    if (newProcedures) setProcedureList([...newProcedures]);
  };

  const procedureName =
    process.env.REACT_APP_THEME === 'lfl'
      ? 'Lines For Life Procedures'
      : 'Proteus Procedures';
  const internalExternalLinks = [
    { id: 1, name: 'Select To what URL should this link go?' },
    { id: 2, name: 'External Website/URL' },
    { id: 3, name: `${procedureName}` },
  ];

  const externalUrlTypes = [
    { id: 1, name: 'Select link type' },
    { id: 2, name: 'http' },
    { id: 3, name: 'https' },
  ];

  const handleLinkText = (text: string) => {
    setLinkText(text);
  };
  const handleExternalUrl = (text: string) => {
    setExternalUrl(text);
  };

  return (
    <SlidePanel
      open={isVisible}
      onClose={() => {
        togglePanel();
        reset();
      }}
      size="lg"
      title="Hyperlink"
      actions={procedureActions()}
    >
      <Stack direction="y" gap={4}>
        <Stack direction="x" gap={3}>
          <FormInput
            name="textToDisplay"
            label="Text to display"
            flex="1"
            value={linkText}
            validation={linkText.length ? undefined : 'error'}
            onTextChange={(text: string) => handleLinkText(text)}
          />
          <FormInput
            name="hyperLinkType"
            label="Hyperlink Type"
            flex="1"
            value="Link outside current procedure"
            disabled={true}
          />
        </Stack>

        <Stack direction="x" gap={3}>
          <FormSelect
            options={internalExternalLinks}
            label="Internal/External link"
            flex="1"
            onChange={(e: any) => {
              setInternalExternalLink(parseInt(e.currentTarget.value));
              setShowSelectedAccountAndProcedure(undefined);
            }}
            value={internalExternalLink}
            noSelectOption
          />
          <Box flex="1" aria-hidden="true"></Box>
        </Stack>

        {internalExternalLink === 2 ? (
          <Stack direction="x" gap={3}>
            <FormSelect
              options={externalUrlTypes}
              label="External Url Type"
              flex="1"
              noSelectOption
              value={selectedExternalUrlType}
              onChange={(e: any) => {
                const nextId = parseInt(e.currentTarget.value);
                setSelectedExternalUrlTypes(nextId);
                setShowSelectedAccountAndProcedure(undefined);
              }}
            />
            <FormInput
              name="url"
              label=" "
              flex="1"
              disabled={selectedExternalUrlType === externalUrlTypes[0].id}
              prefix={
                selectedExternalUrlType !== externalUrlTypes[0].id
                  ? `${
                      externalUrlTypes.find(
                        item => item.id === selectedExternalUrlType,
                      )?.name
                    }://`
                  : ''
              }
              value={externalUrl}
              onTextChange={(text: string) => handleExternalUrl(text)}
            />
          </Stack>
        ) : null}
        {showSelectedAccountAndProcedure ? (
          <Stack direction="y" gap={3}>
            <FormLabel>{`Account: ${showSelectedAccountAndProcedure.linkedAccount}`}</FormLabel>
            <FormLabel>{`Procedure: ${showSelectedAccountAndProcedure.procedureName}`}</FormLabel>
          </Stack>
        ) : (
          <>
            {internalExternalLink === 3 ? (
              <Stack direction="x" gap={3}>
                <Box flex="1">
                  <FormLabel>Choose Account Name</FormLabel>
                  <SearchableList>
                    <ListSearch>
                      <input
                        type="search"
                        placeholder="Search account name"
                        value={keyword}
                        onChange={(e: SyntheticEvent<HTMLInputElement>) =>
                          handleSearchKeyword(e)
                        }
                      />
                      <Icon.Search />
                    </ListSearch>
                    <ListBody ref={tbodyRef}>
                      {(isAccountListLoading || isFetching) && <Loader />}
                      <List>
                        {accountList &&
                          accountList.map((accounts: linkImportAccountList) => (
                            <li
                              className={
                                accounts && accounts.AccountId === activeAccount
                                  ? 'active'
                                  : ''
                              }
                              key={accounts && accounts.AccountId}
                              title={accounts && accounts.AccountNameAndNumber}
                              onClick={() => {
                                if (accounts)
                                  setActiveAccount(accounts.AccountId);
                              }}
                            >
                              {accounts && accounts.AccountNameAndNumber}
                            </li>
                          ))}
                      </List>
                    </ListBody>
                  </SearchableList>
                </Box>
                <Box flex="1">
                  <FormLabel>Choose Procedure Name</FormLabel>
                  <SearchableList>
                    <ListSearch>
                      <input
                        type="search"
                        placeholder="Search procedure names "
                        value={procedureKeyword}
                        onChange={(e: SyntheticEvent<HTMLInputElement>) =>
                          handleProcedureSearch(e)
                        }
                      />
                      <Icon.Search />
                    </ListSearch>
                    <ListBody>
                      {activeAccount ? (
                        <List>
                          {procedureList &&
                            procedureList.map(procedure => {
                              return (
                                <li
                                  className={
                                    procedure.ProcedureId === activeProcedure
                                      ? 'active'
                                      : ''
                                  }
                                  key={`${procedure.ProcedureName} - ${procedure.ProcedureId}`}
                                  title={procedure.ProcedureName}
                                  onClick={() => {
                                    handleSelectedProcedure(procedure);
                                  }}
                                >
                                  {procedure.IsVisible ? (
                                    <Cluster gap={1}>
                                      <div style={{ flexWrap: 'nowrap' }}>
                                        <Icon.Eye fill="var(--color-body-text)" />
                                        <Text
                                          fontWeight="600"
                                          className="truncate"
                                        >
                                          {procedure.ProcedureName}
                                        </Text>
                                      </div>
                                    </Cluster>
                                  ) : (
                                    <Cluster gap={1}>
                                      <div style={{ flexWrap: 'nowrap' }}>
                                        <Icon.EyeOff fill="var(--color-neutral-contrast-05)" />
                                        <Text
                                          style={{ opacity: '0.75' }}
                                          className="truncate"
                                        >
                                          {procedure.ProcedureName}
                                        </Text>
                                      </div>
                                    </Cluster>
                                  )}
                                </li>
                              );
                            })}
                        </List>
                      ) : null}
                    </ListBody>
                  </SearchableList>
                </Box>
              </Stack>
            ) : null}
          </>
        )}
      </Stack>
    </SlidePanel>
  );
}

export default Hyperlink;
